<h2 mat-dialog-title class="flex items-center gap-2 px-6 pt-4 text-2xl">
  <mat-icon aria-hidden="true">{{
    isEditMode ? 'edit' : 'person_add'
  }}</mat-icon>
  {{ isEditMode ? 'Edit User' : 'Invite User' }}
</h2>

<mat-dialog-content class="max-w-md">
  @if (isSubmitting$ | async) {
    <div
      role="status"
      [attr.aria-label]="
        isEditMode
          ? 'Saving changes, please wait'
          : 'Sending invitation, please wait'
      "
      class="absolute top-0 left-0 z-10 flex h-full w-full items-center justify-center bg-white/70"
    >
      <mat-progress-spinner
        aria-hidden="true"
        mode="indeterminate"
        color="primary"
        diameter="50"
      ></mat-progress-spinner>
    </div>
  }

  <form
    [formGroup]="userForm"
    (ngSubmit)="onSave()"
    novalidate
    class="flex flex-col gap-4 px-1 py-2"
  >
    <!-- First Name & Last Name -->
    <div class="flex gap-3">
      <div class="min-w-0 flex-1">
        <label for="firstNameId" class="comcast-input-label">First Name</label>
        <input
          id="firstNameId"
          formControlName="firstName"
          type="text"
          class="comcast-input"
          [ngClass]="{
            'comcast-input-invalid': firstName?.invalid && firstName?.touched,
          }"
          placeholder="Enter first name"
          aria-required="true"
          [attr.aria-invalid]="firstName?.invalid && firstName?.touched"
          aria-describedby="firstName-error"
        />
        <div id="firstName-error" aria-live="polite">
          @if (firstName?.invalid && firstName?.touched) {
            <span class="comcast-validation-error">First name is required</span>
          }
        </div>
      </div>

      <div class="min-w-0 flex-1">
        <label for="lastNameId" class="comcast-input-label">Last Name</label>
        <input
          id="lastNameId"
          formControlName="lastName"
          type="text"
          class="comcast-input"
          [ngClass]="{
            'comcast-input-invalid': lastName?.invalid && lastName?.touched,
          }"
          placeholder="Enter last name"
          aria-required="true"
          [attr.aria-invalid]="lastName?.invalid && lastName?.touched"
          aria-describedby="lastName-error"
        />
        <div id="lastName-error" aria-live="polite">
          @if (lastName?.invalid && lastName?.touched) {
            <span class="comcast-validation-error">Last name is required</span>
          }
        </div>
      </div>
    </div>

    <!-- Email -->
    <div>
      <label for="emailId" class="comcast-input-label">
        Email
        @if (isEditMode) {
          <span class="ml-1 text-xs text-gray-500">(Read-only)</span>
        }
      </label>
      <input
        id="emailId"
        formControlName="email"
        type="email"
        class="comcast-input disabled:cursor-not-allowed disabled:opacity-60"
        [ngClass]="{
          'comcast-input-invalid':
            email?.touched &&
            (email?.hasError('required') ||
              email?.hasError('email') ||
              email?.hasError('publicEmailDomain')),
        }"
        [placeholder]="isInternalOnly() ? 'Enter email' : 'Enter company email'"
        autocomplete="email"
        aria-required="true"
        [attr.aria-invalid]="email?.touched && email?.invalid"
        aria-describedby="email-hint email-error"
      />
      @if (!isInternalOnly()) {
        <p id="email-hint" class="mt-1 text-xs text-gray-400">
          No public email domains (Gmail, Yahoo, Outlook, etc.).
        </p>
      }
      @if (
        email?.touched &&
        (email?.hasError('required') || email?.hasError('email'))
      ) {
        <div id="email-error" role="alert" class="comcast-validation-error">
          A valid email is required
        </div>
      } @else if (email?.touched && email?.hasError('publicEmailDomain')) {
        <div id="email-error" role="alert" class="comcast-validation-error">
          Please use a company email address (no public domains).
        </div>
      }
    </div>

    <!-- Username -->
    @if (isEditMode || isInternalOnly()) {
      <div>
        <label for="usernameId" class="comcast-input-label">
          Username
          @if (!isEditMode && isInternalOnly()) {
            <span class="ml-1 text-xs font-medium text-blue-600">
              (Editable - Internal Only)
            </span>
          } @else if (isEditMode) {
            <span class="ml-1 text-xs text-gray-500">(Read-only)</span>
          }
        </label>
        <input
          id="usernameId"
          formControlName="username"
          type="email"
          class="comcast-input disabled:cursor-not-allowed disabled:opacity-60"
          [ngClass]="{
            'comcast-input-invalid':
              !isEditMode && username?.invalid && username?.touched,
          }"
          placeholder="Enter username"
          [attr.aria-invalid]="
            !isEditMode && username?.invalid && username?.touched
          "
          aria-describedby="username-error"
        />
        @if (
          !isEditMode &&
          ((username?.hasError('required') && username?.touched) ||
            (username?.hasError('email') && username?.touched))
        ) {
          <div
            id="username-error"
            role="alert"
            class="comcast-validation-error"
          >
            @if (username?.hasError('required')) {
              Username is required
            } @else if (username?.hasError('email')) {
              Username must be a valid email
            }
          </div>
        }
      </div>
    }

    <!-- Role -->
    <div>
      <label id="roleLabel" for="role-trigger" class="comcast-input-label">
        Role
      </label>
      <div class="mt-2">
        <button
          id="role-trigger"
          type="button"
          class="comcast-select-trigger"
          cdkOverlayOrigin
          #roleAnchor="cdkOverlayOrigin"
          #triggerEl
          [class.comcast-select-trigger--open]="isRoleDropdownOpen()"
          [class.comcast-select-trigger--filled]="!!selectedRoleLabel()"
          [class.comcast-input-invalid]="
            portalRole?.invalid && portalRole?.touched
          "
          [attr.aria-expanded]="isRoleDropdownOpen()"
          [attr.aria-invalid]="portalRole?.invalid && portalRole?.touched"
          [attr.aria-describedby]="
            portalRole?.invalid && portalRole?.touched ? 'role-error' : null
          "
          aria-haspopup="listbox"
          aria-controls="role-listbox"
          aria-required="true"
          aria-labelledby="roleLabel"
          (click)="toggleRoleDropdown($event)"
        >
          <span [class.text-brand-muted]="!selectedRoleLabel()">
            {{ selectedRoleLabel() || 'Select user role' }}
          </span>
          <mat-icon class="comcast-select-icon" aria-hidden="true">
            {{ isRoleDropdownOpen() ? 'arrow_drop_up' : 'arrow_drop_down' }}
          </mat-icon>
        </button>

        <ng-template
          cdkConnectedOverlay
          [cdkConnectedOverlayOrigin]="roleAnchor"
          [cdkConnectedOverlayOpen]="isRoleDropdownOpen()"
          [cdkConnectedOverlayWidth]="triggerEl.offsetWidth"
          [cdkConnectedOverlayPositions]="roleDropdownPositions"
          [cdkConnectedOverlayHasBackdrop]="true"
          cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
          (backdropClick)="onRoleDropdownClose()"
          (detach)="onRoleDropdownClose()"
        >
          <div
            id="role-listbox"
            class="comcast-select-panel comcast-select-panel--open"
            style="top: 0"
            [class.comcast-select-panel--invalid]="
              portalRole?.invalid && portalRole?.touched
            "
            role="listbox"
            aria-labelledby="roleLabel"
          >
            <!-- a11y listbox pattern: docs/accessibility/custom-listbox.md -->
            <div class="comcast-select-options">
              @for (role of portalRoles$ | async; track role.Id) {
                @if (role.Name) {
                  <div
                    class="comcast-select-option"
                    [class.comcast-select-option--selected]="
                      selectedRole()?.Id === role.Id
                    "
                    [class.cursor-not-allowed]="
                      isAdminOptionDisabled(role.Name)
                    "
                    [class.opacity-50]="isAdminOptionDisabled(role.Name)"
                    role="option"
                    tabindex="0"
                    [attr.aria-disabled]="isAdminOptionDisabled(role.Name)"
                    [attr.aria-selected]="selectedRole()?.Id === role.Id"
                    (click)="selectPortalRole(role, $event)"
                    (keydown.enter)="selectPortalRole(role, $event)"
                    (keydown.space)="selectPortalRole(role, $event)"
                  >
                    {{ role.Name }}
                  </div>
                }
              }
            </div>
          </div>
        </ng-template>
      </div>
      @if (portalRole?.invalid && portalRole?.touched) {
        <div id="role-error" role="alert" class="comcast-validation-error">
          Role is required
        </div>
      }
    </div>

    <!-- Submission error -->
    @if (hasSubmissionError$ | async) {
      <div
        role="alert"
        class="flex rounded-xs border border-red-300 bg-red-100 p-2 text-sm text-red-800"
      >
        <span>{{ errorMessage }}</span>
      </div>
    }

    <mat-dialog-actions>
      <div class="flex w-full justify-end gap-2">
        <button type="button" class="btn-secondary" [mat-dialog-close]="false">
          Cancel
        </button>

        <button
          class="btn-primary disabled:cursor-not-allowed disabled:bg-gray-400 disabled:hover:bg-gray-400"
          type="submit"
          [disabled]="(isSubmitting$ | async) || isDemoAccount"
          [attr.aria-busy]="isSubmitting$ | async"
        >
          {{ isEditMode ? 'Save changes' : 'Send invitation' }}
        </button>
      </div>
    </mat-dialog-actions>
  </form>
</mat-dialog-content>
